import { useState, useEffect, useRef } from 'react';
import { api } from '../../../api/client';
import {
  Save, X, Pause, Play, XCircle, Trash2, AlertTriangle, Image, Loader2,
  ArrowUp, ArrowDown, Upload, Link as LinkIcon, Plus
} from 'lucide-react';
import { useCan, Can } from '../../../lib/rbac';

interface EditMLTabProps {
  product: any;
  sellerId: number;
  onRefresh: () => void;
}

export default function EditMLTab({ product, sellerId, onRefresh }: EditMLTabProps) {
  const can = useCan();
  const canEdit = can('catalog.edit_listing');
  const hasQuickActions = can('catalog.pause_activate') || can('catalog.close_delete');
  const [changes, setChanges] = useState<Record<string, any>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; errors?: string[] } | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [mlDetails, setMlDetails] = useState<{ description: string; seller_sku: string; variations: any[]; pictures?: Array<{ id: string; url?: string; secure_url?: string }>; item_attributes?: any[]; category_attributes?: any[] } | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(true);

  useEffect(() => {
    const loadDetails = async () => {
      setDetailsLoading(true);
      try {
        const res = await api.get<any>(`/catalog/products/${product.item_id}/ml/details?seller_id=${sellerId}`);
        if (res.success && res.data) {
          setMlDetails(res.data);
        }
      } catch { /* ignore */ }
      setDetailsLoading(false);
    };
    loadDetails();
  }, [product.item_id, sellerId]);

  const hasChanges = Object.keys(changes).length > 0;
  const changeCount = Object.keys(changes).length;

  const updateField = (field: string, value: any) => {
    setChanges(prev => {
      const next = { ...prev };
      if (value === product[field]) {
        delete next[field];
      } else {
        next[field] = value;
      }
      return next;
    });
  };

  const applyChanges = async () => {
    if (!hasChanges) return;
    if (!confirm('¿Aplicar cambios a MercadoLibre?')) return;
    setSubmitting(true);
    setResult(null);
    try {
      const res = await api.put(`/catalog/products/${product.item_id}/ml/update?seller_id=${sellerId}`, changes);
      setResult(res.success
        ? { success: true, errors: (res.data as any)?.errors }
        : { success: false, errors: [(res as any).error || 'Error desconocido'] }
      );
      if (res.success) {
        setChanges({});
        onRefresh();
      }
    } catch {
      setResult({ success: false, errors: ['Error de conexión'] });
    } finally {
      setSubmitting(false);
    }
  };

  const executeAction = async (action: string) => {
    if (action === 'closed' && !confirm('¿Cerrar este listing? Esta acción no se puede deshacer fácilmente.')) return;
    if (action === 'deleted' && !confirm('¿ELIMINAR este listing permanentemente? Esta acción NO se puede deshacer.')) return;
    setActionLoading(action);
    setResult(null);
    try {
      const res = await api.put(`/catalog/products/${product.item_id}/ml/status?seller_id=${sellerId}`, { status: action });
      setResult(res.success
        ? { success: true }
        : { success: false, errors: [(res as any).error || 'Error al cambiar status'] }
      );
      if (res.success) onRefresh();
    } catch {
      setResult({ success: false, errors: ['Error de conexión'] });
    } finally {
      setActionLoading(null);
    }
  };

  const changedClass = 'border-l-4 border-l-amber-400 bg-amber-50/10';

  return (
    <div className="space-y-6">
      {/* Quick Actions — cada botón pide su capacidad; sin ninguna, no se muestra la barra */}
      {hasQuickActions && (
      <div>
        <h3 className="text-sm font-semibold text-[var(--color-text)] mb-3 flex items-center gap-2">
          <AlertTriangle size={16} />
          Acciones Rápidas
        </h3>
        <div className="flex flex-wrap gap-2">
          <Can cap="catalog.pause_activate">
          <button
            onClick={() => executeAction('paused')}
            disabled={actionLoading !== null || product.status === 'paused'}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border border-[var(--color-border)] bg-[var(--color-surface)] hover:bg-[var(--color-surface-hover)] text-[var(--color-warning)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {actionLoading === 'paused' ? (
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <Pause size={16} />
            )}
            Pausar
          </button>
          <button
            onClick={() => executeAction('active')}
            disabled={actionLoading !== null || product.status === 'active'}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border border-[var(--color-border)] bg-[var(--color-surface)] hover:bg-[var(--color-surface-hover)] text-[var(--color-success)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {actionLoading === 'active' ? (
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <Play size={16} />
            )}
            Activar
          </button>
          </Can>
          <Can cap="catalog.close_delete">
          <button
            onClick={() => executeAction('closed')}
            disabled={actionLoading !== null || product.status === 'closed'}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border border-[var(--color-border)] bg-[var(--color-surface)] hover:bg-[var(--color-surface-hover)] text-[var(--color-error)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {actionLoading === 'closed' ? (
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <XCircle size={16} />
            )}
            Cerrar
          </button>
          <button
            onClick={() => executeAction('deleted')}
            disabled={actionLoading !== null || product.status === 'deleted'}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border border-red-500/50 bg-red-500/10 hover:bg-red-500/20 text-red-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {actionLoading === 'deleted' ? (
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <Trash2 size={16} />
            )}
            Eliminar
          </button>
          </Can>
        </div>
      </div>
      )}

      <hr className="border-[var(--color-border)]" />

      {/* Editable Fields */}
      <div className="space-y-4">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-[var(--color-text)] mb-1">Título</label>
          <input
            type="text"
            maxLength={250}
            value={changes.title !== undefined ? changes.title : (product.title || '')}
            onChange={(e) => updateField('title', e.target.value)}
            readOnly={!canEdit}
            className={`w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30 focus:border-[var(--color-primary)] ${changes.title !== undefined ? changedClass : ''}`}
          />
          <div className="flex justify-between mt-1">
            <span className="text-xs text-[var(--color-text-muted)]">Máximo 250 caracteres</span>
            <span className="text-xs text-[var(--color-text-muted)]">
              {(changes.title !== undefined ? changes.title : (product.title || '')).length}/250
            </span>
          </div>
        </div>

        {/* SKU Seller */}
        <div>
          <label className="block text-sm font-medium text-[var(--color-text)] mb-1 flex items-center gap-1.5">
            SKU Seller
            {detailsLoading && <Loader2 size={12} className="animate-spin text-[var(--color-text-muted)]" />}
          </label>
          <input
            type="text"
            value={changes.seller_custom_field !== undefined ? changes.seller_custom_field : (mlDetails?.seller_sku || product.seller_custom_field || '')}
            onChange={(e) => updateField('seller_custom_field', e.target.value)}
            readOnly={!canEdit}
            className={`w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30 focus:border-[var(--color-primary)] ${changes.seller_custom_field !== undefined ? changedClass : ''}`}
          />
        </div>

        {/* Attributes — right after SKU */}
        <div>
          <label className="block text-sm font-medium text-[var(--color-text)] mb-2 flex items-center gap-1.5">
            Atributos
            {detailsLoading && <Loader2 size={12} className="animate-spin text-[var(--color-text-muted)]" />}
          </label>
          {!detailsLoading && mlDetails?.category_attributes && mlDetails.category_attributes.length > 0 ? (
            <div className="space-y-3">
              {(() => {
                // Build map of current item attribute values
                const currentAttrs: Record<string, { value_id?: string; value_name?: string }> = {};
                for (const a of (mlDetails.item_attributes || [])) {
                  currentAttrs[a.id] = { value_id: a.value_id, value_name: a.value_name };
                }

                // Filter: show editable attributes (not hidden, not read_only, not SELLER_SKU which is above)
                const editableAttrs = mlDetails.category_attributes.filter((ca: any) => {
                  if (ca.id === 'SELLER_SKU') return false;
                  if (ca.id === 'ITEM_CONDITION') return false;
                  if (ca.tags?.hidden) return false;
                  if (ca.tags?.read_only) return false;
                  if (ca.tags?.allow_variations) return false; // variation attrs go in variations
                  return true;
                });

                // Sort: required first, then by relevance
                editableAttrs.sort((a: any, b: any) => {
                  const aReq = a.tags?.required || a.tags?.catalog_required ? 0 : 1;
                  const bReq = b.tags?.required || b.tags?.catalog_required ? 0 : 1;
                  if (aReq !== bReq) return aReq - bReq;
                  return (a.relevance || 99) - (b.relevance || 99);
                });

                // Get changed attributes
                const changedAttributes: any[] = changes.attributes || [];

                const getAttrValue = (attrId: string) => {
                  const changed = changedAttributes.find((a: any) => a.id === attrId);
                  if (changed) return changed.value_name || '';
                  return currentAttrs[attrId]?.value_name || '';
                };

                const setAttrValue = (attrId: string, valueName: string, valueId?: string) => {
                  const original = currentAttrs[attrId]?.value_name || '';
                  const existingChanges = changes.attributes || [];

                  if (valueName === original) {
                    // Remove from changes
                    const filtered = existingChanges.filter((a: any) => a.id !== attrId);
                    if (filtered.length === 0) {
                      setChanges(prev => { const next = { ...prev }; delete next.attributes; return next; });
                    } else {
                      setChanges(prev => ({ ...prev, attributes: filtered }));
                    }
                  } else {
                    const updated = existingChanges.filter((a: any) => a.id !== attrId);
                    const entry: any = { id: attrId, value_name: valueName };
                    if (valueId) entry.value_id = valueId;
                    updated.push(entry);
                    setChanges(prev => ({ ...prev, attributes: updated }));
                  }
                };

                const isAttrChanged = (attrId: string) => changedAttributes.some((a: any) => a.id === attrId);

                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {editableAttrs.map((ca: any) => {
                      const isRequired = ca.tags?.required || ca.tags?.catalog_required;
                      const hasValues = ca.values && ca.values.length > 0;
                      const currentVal = getAttrValue(ca.id);
                      const changed = isAttrChanged(ca.id);

                      return (
                        <div key={ca.id} className={`p-3 rounded-lg border bg-[var(--color-bg)] ${changed ? 'border-l-4 border-l-amber-400 border-amber-400/30' : 'border-[var(--color-border-light)]'}`}>
                          <label className="flex items-center gap-1.5 text-xs font-medium text-[var(--color-text-muted)] mb-1">
                            {ca.name}
                            {isRequired && <span className="text-[var(--color-error)]">*</span>}
                          </label>
                          {hasValues && ca.value_type !== 'number' && ca.value_type !== 'number_unit' ? (
                            <select
                              value={currentVal}
                              disabled={!canEdit}
                              onChange={(e) => {
                                const selected = ca.values.find((v: any) => v.name === e.target.value);
                                setAttrValue(ca.id, e.target.value, selected?.id);
                              }}
                              className="w-full px-2 py-1.5 rounded border border-[var(--color-border)] bg-[var(--color-surface)] text-sm text-[var(--color-text)]"
                            >
                              <option value="">— Seleccionar —</option>
                              {ca.values.map((v: any) => (
                                <option key={v.id || v.name} value={v.name}>{v.name}</option>
                              ))}
                              {currentVal && !ca.values.find((v: any) => v.name === currentVal) && (
                                <option value={currentVal}>{currentVal} (actual)</option>
                              )}
                            </select>
                          ) : (
                            <input
                              type={ca.value_type === 'number' || ca.value_type === 'number_unit' ? 'text' : 'text'}
                              value={currentVal}
                              readOnly={!canEdit}
                              onChange={(e) => setAttrValue(ca.id, e.target.value)}
                              placeholder={ca.hint || ca.name}
                              className="w-full px-2 py-1.5 rounded border border-[var(--color-border)] bg-[var(--color-surface)] text-sm text-[var(--color-text)]"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          ) : !detailsLoading ? (
            <p className="text-sm text-[var(--color-text-muted)]">No se pudieron cargar los atributos de la categoría</p>
          ) : null}
        </div>

        {/* Pictures */}
        <PicturesEditor
          cbtItemId={product.item_id}
          sellerId={sellerId}
          initialPictures={mlDetails?.pictures || []}
          onApplied={onRefresh}
        />

        {/* Description — formatted view + edit toggle */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm font-medium text-[var(--color-text)] flex items-center gap-1.5">
              Descripción
              {detailsLoading && <Loader2 size={12} className="animate-spin text-[var(--color-text-muted)]" />}
            </label>
            {!detailsLoading && canEdit && (
              <button
                onClick={() => {
                  if (changes.description === undefined) {
                    setChanges(prev => ({ ...prev, description: mlDetails?.description || '' }));
                  } else {
                    setChanges(prev => { const next = { ...prev }; delete next.description; return next; });
                  }
                }}
                className="text-xs px-2 py-1 rounded border border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] transition-colors"
              >
                {changes.description !== undefined ? 'Cancelar edición' : 'Editar'}
              </button>
            )}
          </div>
          {changes.description !== undefined ? (
            <textarea
              rows={15}
              value={changes.description}
              onChange={(e) => {
                const original = mlDetails?.description || '';
                if (e.target.value === original) {
                  setChanges(prev => { const next = { ...prev }; delete next.description; return next; });
                } else {
                  setChanges(prev => ({ ...prev, description: e.target.value }));
                }
              }}
              className={`w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30 focus:border-[var(--color-primary)] resize-y ${changedClass}`}
            />
          ) : (
            <div className="w-full px-4 py-3 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)] text-sm text-[var(--color-text)] whitespace-pre-wrap max-h-96 overflow-y-auto leading-relaxed">
              {detailsLoading ? (
                <span className="text-[var(--color-text-muted)]">Cargando descripción...</span>
              ) : mlDetails?.description ? (
                mlDetails.description
              ) : (
                <span className="text-[var(--color-text-muted)] italic">Sin descripción</span>
              )}
            </div>
          )}
        </div>
      </div>

      <hr className="border-[var(--color-border)]" />

      {/* Pending Changes Panel — guardar textos/atributos pide la llave de editar publicaciones */}
      <Can cap="catalog.edit_listing">
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            {hasChanges ? (
              <>
                <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                <span className="text-sm font-medium text-[var(--color-text)]">
                  {changeCount} cambio{changeCount !== 1 ? 's' : ''} pendiente{changeCount !== 1 ? 's' : ''}
                </span>
              </>
            ) : (
              <span className="text-sm text-[var(--color-text-muted)]">Sin cambios pendientes</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {hasChanges && (
              <button
                onClick={() => setChanges({})}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] transition-colors"
              >
                <X size={14} />
                Descartar
              </button>
            )}
            <button
              onClick={applyChanges}
              disabled={!hasChanges || submitting}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save size={14} />
              )}
              Aplicar cambios a ML
            </button>
          </div>
        </div>

        {/* Result message */}
        {result && (
          <div className={`mt-3 p-3 rounded-lg text-sm ${
            result.success
              ? 'bg-green-500/10 border border-green-500/20 text-green-400'
              : 'bg-red-500/10 border border-red-500/20 text-red-400'
          }`}>
            {result.success ? (
              <span>Cambios aplicados correctamente</span>
            ) : (
              <div>
                <span className="font-medium">Error al aplicar cambios:</span>
                {result.errors?.map((err, i) => (
                  <div key={i} className="mt-1">{err}</div>
                ))}
              </div>
            )}
            {result.success && result.errors && result.errors.length > 0 && (
              <div className="mt-1 text-amber-400">
                <span className="font-medium">Advertencias:</span>
                {result.errors.map((err, i) => (
                  <div key={i} className="mt-0.5">{err}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      </Can>
    </div>
  );
}

// ── PicturesEditor ────────────────────────────────────────────────────────────
// Stage-mode: changes accumulate locally; "Aplicar a CBT + sites" sends them.
// Operations: add via file upload, add via URL, reorder with ↑/↓, delete.
// Propagation: server applies to CBT parent + each active site_item (ADR-26).

interface MLPicture {
  id: string;
  url?: string;
  secure_url?: string;
}

function PicturesEditor({
  cbtItemId,
  sellerId,
  initialPictures,
  onApplied,
}: {
  cbtItemId: string;
  sellerId: number;
  initialPictures: MLPicture[];
  onApplied: () => void;
}) {
  const [pics, setPics] = useState<MLPicture[]>(initialPictures);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState<'idle' | 'uploading' | 'applying'>('idle');
  const [urlInput, setUrlInput] = useState('');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Sync when parent re-loads details (after Aplicar)
  useEffect(() => {
    setPics(initialPictures);
    setDirty(false);
  }, [initialPictures]);

  const can = useCan();
  const isCbtItem = /^CBT/.test(cbtItemId);
  // Editar fotos (subir / ordenar / eliminar / propagar) pide la llave de editar publicaciones.
  const isCbt = isCbtItem && can('catalog.edit_listing');

  const previewSrc = (p: MLPicture) => p.secure_url || p.url || '';

  function move(idx: number, delta: number) {
    const j = idx + delta;
    if (j < 0 || j >= pics.length) return;
    const next = pics.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    setPics(next);
    setDirty(true);
    setMsg(null);
  }

  function remove(idx: number) {
    const next = pics.slice();
    next.splice(idx, 1);
    setPics(next);
    setDirty(true);
    setMsg(null);
  }

  async function uploadFile(file: File) {
    if (!isCbt) {
      setMsg({ kind: 'err', text: 'Solo se pueden editar imágenes desde el CBT padre.' });
      return;
    }
    setBusy('uploading');
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.postFormData<{ picture_id: string; url: string }>(
        `/mercadolibre/listings/items/${cbtItemId}/pictures/upload?seller_id=${sellerId}`,
        fd
      );
      if (res.success && res.data) {
        setPics(prev => [...prev, { id: res.data!.picture_id, secure_url: res.data!.url }]);
        setDirty(true);
        setMsg({ kind: 'ok', text: 'Imagen subida. Ordenala y aplicá los cambios.' });
      } else {
        setMsg({ kind: 'err', text: res.error || 'Error al subir' });
      }
    } catch (err) {
      setMsg({ kind: 'err', text: err instanceof Error ? err.message : 'network error' });
    } finally {
      setBusy('idle');
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function addFromUrl() {
    if (!isCbt) {
      setMsg({ kind: 'err', text: 'Solo se pueden editar imágenes desde el CBT padre.' });
      return;
    }
    const url = urlInput.trim();
    if (!/^https?:\/\//i.test(url)) {
      setMsg({ kind: 'err', text: 'URL inválida' });
      return;
    }
    setBusy('uploading');
    setMsg(null);
    try {
      const res = await api.post<{ picture_id: string; url: string }>(
        `/mercadolibre/listings/items/${cbtItemId}/pictures/from-url?seller_id=${sellerId}`,
        { url }
      );
      if (res.success && res.data) {
        setPics(prev => [...prev, { id: res.data!.picture_id, secure_url: res.data!.url }]);
        setDirty(true);
        setUrlInput('');
        setMsg({ kind: 'ok', text: 'Imagen subida desde URL.' });
      } else {
        setMsg({ kind: 'err', text: res.error || 'Error al subir desde URL' });
      }
    } catch (err) {
      setMsg({ kind: 'err', text: err instanceof Error ? err.message : 'network error' });
    } finally {
      setBusy('idle');
    }
  }

  async function applyChangesToML() {
    if (!isCbt) return;
    if (pics.length === 0) {
      setMsg({ kind: 'err', text: 'Necesitás al menos 1 imagen' });
      return;
    }
    setBusy('applying');
    setMsg(null);
    try {
      const res = await api.put<{ propagation: { success: number; failed: number; errors: string[] }; total_pictures: number }>(
        `/mercadolibre/listings/items/${cbtItemId}/pictures?seller_id=${sellerId}`,
        { pictureIds: pics.map(p => p.id) }
      );
      if (res.success && res.data) {
        const { propagation, total_pictures } = res.data;
        setMsg({
          kind: propagation.failed === 0 ? 'ok' : 'err',
          text: `CBT + ${propagation.success} sites OK (${total_pictures} imgs)${propagation.failed > 0 ? ` · ${propagation.failed} fallaron: ${propagation.errors.slice(0, 2).join(' | ')}` : ''}`,
        });
        setDirty(false);
        onApplied();
      } else {
        setMsg({ kind: 'err', text: res.error || 'Error al aplicar' });
      }
    } catch (err) {
      setMsg({ kind: 'err', text: err instanceof Error ? err.message : 'network error' });
    } finally {
      setBusy('idle');
    }
  }

  function reset() {
    setPics(initialPictures);
    setDirty(false);
    setMsg(null);
  }

  return (
    <div>
      <label className="block text-sm font-medium text-[var(--color-text)] mb-2 flex items-center gap-1.5">
        <Image size={14} />
        Imágenes ({pics.length})
        {!isCbtItem && <span className="text-[10px] text-[var(--color-text-muted)] italic">— solo lectura para site_items</span>}
      </label>

      {pics.length > 0 ? (
        <div className="flex flex-wrap gap-3 mb-3">
          {pics.map((p, i) => (
            <div
              key={`${p.id}-${i}`}
              className="relative group w-24 h-24 rounded-lg border border-[var(--color-border)] bg-white overflow-hidden"
            >
              <img src={previewSrc(p)} alt={`#${i + 1}`} className="w-full h-full object-contain" />
              <span className="absolute top-1 left-1 bg-black/60 text-white text-[10px] rounded px-1.5 py-0.5">
                {i + 1}
              </span>
              {isCbt && (
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                  <button
                    type="button"
                    title="Subir"
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                    className="p-1 rounded bg-white/90 text-black disabled:opacity-30 hover:bg-white"
                  >
                    <ArrowUp size={12} />
                  </button>
                  <button
                    type="button"
                    title="Bajar"
                    disabled={i === pics.length - 1}
                    onClick={() => move(i, 1)}
                    className="p-1 rounded bg-white/90 text-black disabled:opacity-30 hover:bg-white"
                  >
                    <ArrowDown size={12} />
                  </button>
                  <button
                    type="button"
                    title="Eliminar"
                    onClick={() => remove(i)}
                    className="p-1 rounded bg-red-500 text-white hover:bg-red-600"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-[var(--color-text-muted)] mb-3">Sin imágenes</div>
      )}

      {isCbt && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={e => { const f = e.target.files?.[0]; if (f) uploadFile(f); }}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy !== 'idle'}
              className="text-xs px-3 py-1.5 rounded border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] flex items-center gap-1.5 disabled:opacity-50"
            >
              {busy === 'uploading' ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              Subir archivo (JPG/PNG, ≤10MB)
            </button>

            <div className="flex items-center gap-1">
              <LinkIcon size={12} className="text-[var(--color-text-muted)]" />
              <input
                type="url"
                value={urlInput}
                onChange={e => setUrlInput(e.target.value)}
                placeholder="https://..."
                disabled={busy !== 'idle'}
                className="text-xs px-2 py-1.5 rounded border border-[var(--color-border)] bg-[var(--color-bg)] w-64"
              />
              <button
                type="button"
                onClick={addFromUrl}
                disabled={busy !== 'idle' || !urlInput.trim()}
                className="text-xs px-2 py-1.5 rounded border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] flex items-center gap-1 disabled:opacity-50"
              >
                <Plus size={12} /> Agregar
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={applyChangesToML}
              disabled={!dirty || busy !== 'idle' || pics.length === 0}
              className="text-xs px-3 py-1.5 rounded bg-[var(--color-primary)] text-white font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {busy === 'applying' ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Aplicar a CBT + sites
            </button>
            {dirty && (
              <button
                type="button"
                onClick={reset}
                disabled={busy !== 'idle'}
                className="text-xs px-3 py-1.5 rounded border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] flex items-center gap-1.5"
              >
                <X size={12} /> Cancelar cambios
              </button>
            )}
            {dirty && <span className="text-[11px] text-[var(--color-warning)]">Cambios sin aplicar</span>}
          </div>
        </>
      )}

      {msg && (
        <div className={`mt-2 text-xs p-2 rounded ${msg.kind === 'ok' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {msg.text}
        </div>
      )}
    </div>
  );
}
